import { FC, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { NonPlannedHour } from 'components/types';

import { toDecimalPlaces } from 'utils/misc';

import type { SaveAndUpdateOptions } from '../types';
import NonPlannedHoursDialog from './NonPlannedHoursDialog';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

interface NonPlannedHoursGridProps {
  nonPlannedHours: NonPlannedHour[];
  saveAndUpdate: (params: SaveAndUpdateOptions) => Promise<void>;
  loading: boolean;
  disableActions?: boolean;
}

type NonPlannedHourToEdit = NonPlannedHour & { rowId?: number };

const NonPlannedHoursGrid: FC<NonPlannedHoursGridProps> = ({
  nonPlannedHours,
  saveAndUpdate,
  loading,
  disableActions
}) => {
  const { t } = useTranslation('common');
  const [dialogOpen, setDialogOpen] = useState(false);

  const [nonPlannedHourToEdit, setNonPlannedHourToEdit] =
    useState<NonPlannedHourToEdit | null>(null);

  const columns = [
    { field: 'work', headerName: t('work'), flex: 1, minWidth: 150 },
    { field: 'function', headerName: t('function'), flex: 1, minWidth: 150 },
    {
      field: 'dutyHourType',
      headerName: t('dutyHourType'),
      flex: 1,
      minWidth: 150
    },
    { field: 'category', headerName: t('category'), flex: 1, minWidth: 150 },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      minWidth: 150
    },
    { field: 'amount', headerName: t('amount') },
    { field: 'hours', headerName: t('hours') },
    { field: 'unit', headerName: t('unit') },
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      valueFormatter: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      valueFormatter: dateFormatter
    },
    { field: 'hoursPerYear', headerName: t('hoursPerYear') }
  ];

  const rows = nonPlannedHours.map((n) => ({
    id: n.id,
    work: n.costCenter.name,
    function: n.function.name,
    dutyHourType: n.dhtName,
    category: n.dhtHoursCategory
      ? t(`dhtCategories.${n.dhtHoursCategory}`)
      : '',
    description: n.description,
    amount: n.quantity.toFixed(2),
    hours: n.hoursPerUnit.toFixed(2),
    unit: t(`timeUnits.${n.unit}`),
    validFrom: n.timeframe.from,
    validTo: n.timeframe.to,
    hoursPerYear: toDecimalPlaces(n.hoursYearTotal)
  }));

  const onAddClick = () => {
    setNonPlannedHourToEdit(null);
    setDialogOpen(true);
  };

  const onCopyClick = (id: number) => {
    const nonPlannedHour = nonPlannedHours.find(({ id: _id }) => id === _id);
    if (nonPlannedHour) {
      setNonPlannedHourToEdit({ ...nonPlannedHour, rowId: id });
    }

    setDialogOpen(true);
  };

  const onEditClick = (id: number) => {
    const nonPlannedHour = nonPlannedHours.find(({ id: _id }) => id === _id);

    if (nonPlannedHour) {
      setNonPlannedHourToEdit(nonPlannedHour);
    }

    setDialogOpen(true);
  };

  const onDeleteClick = (idsList: number[]) => {
    saveAndUpdate({ req: 'DELETE', idsList });
  };

  return (
    <TableCRUDLayout
      columns={columns}
      rows={rows}
      shouldRenderToolBar={false}
      renderFormContainer={false}
      editable={false}
      disableActions={disableActions}
      onAddClick={onAddClick}
      onCopyClick={(id) => onCopyClick(id as number)}
      onEditClick={(id) => onEditClick(id as number)}
      onDeleteClick={(idsList) => onDeleteClick(idsList as number[])}
      checkboxSelection
      confirmationTitle={t('baseplan.nonPlannedHoursDialog.confirmationTitle')}
      confirmationBody={t('baseplan.nonPlannedHoursDialog.confirmationBody')}
      addRowDialog={
        <NonPlannedHoursDialog
          editValues={nonPlannedHourToEdit}
          isOpen={dialogOpen}
          handleClose={() => setDialogOpen(false)}
          saveAndUpdate={saveAndUpdate}
          loading={loading}
        />
      }
    />
  );
};

export default NonPlannedHoursGrid;
